import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMatchMediaQuery } from '@app/hooks/useMatchMedia';
import { analyticsService } from '@common/analytics';
import AppLayout from '../shared/content';
import {
  advisorsSectionThemes, sectionIds, sectionTypes
} from './prop_types';
import AdvisorSection from './nested_views/advisors_section';
import HeroBanner from './nested_views/hero_banner';
import classes from './classes.module.scss';
import SpecialitiesSection from './nested_views/specialities_section';
import StaffPickSection from './nested_views/staff_pick_section';
import PersonalSection from './nested_views/personal_section';
import AffiliatePromoPopup from '../affiliate_promo_popup';
import {
  advisorsEmptyList, renderCanonicalLink
} from '../../config/util';
import ButtonSection from './nested_views/button_section/button_section';
import ButtonV2Section from './nested_views/button_v2_section';
import AllAdvisorsSection from './nested_views/all_advisors_section';
import { itemProp } from '../../actions/home_page';
import { promotionLabelTypeScreens } from '../../config/const';
import PromotionLabel from './nested_views/promotion_label';
import { FiltersSection } from './nested_views/filters_section';
import SatisfactionGuaranteedSection from './nested_views/satisfaction_guaranteed_section';
import LoyaltySection from './nested_views/loyalty_section';

const getAdvisorSectionTheme = (section) => {
  if (section.id === sectionIds.advisorsFromCountry) {
    return advisorsSectionThemes.advisors_from_country;
  }
  if (advisorsSectionThemes[section.type] === null) {
    return advisorsSectionThemes.badge;
  }
  if (advisorsSectionThemes[`${ section.type }_${ section.subtype }`]) {
    return advisorsSectionThemes[`${ section.type }_${ section.subtype }`];
  }
  return advisorsSectionThemes[section.type];
};

const getIdentifySection = (section, showPersonalSectionBrowser, sectionIndex, listIndex) => {
  const sectionKey = `${ section.id }_${ listIndex }`;
  const identifySections = {
    [sectionIds.staffPick]: <StaffPickSection key={ sectionKey } { ...section } sectionIndex={ sectionIndex } />,
    [sectionIds.specialities]: <SpecialitiesSection key={ sectionKey } { ...section } index={ sectionIndex } />,
    [sectionTypes.personal]: <PersonalSection key={ sectionKey } index={ sectionIndex } />,
    [sectionIds.all_advisors]: <ButtonSection key={ sectionKey } { ...section } index={ sectionIndex } />,
    [sectionIds.allAdvisorsList]: <AllAdvisorsSection key={ sectionKey } { ...section } index={ sectionIndex } />,
    [sectionIds.filters]: <FiltersSection key={ sectionKey } { ...section } index={ sectionIndex } />,
    [sectionIds.advisorVetting]: <ButtonV2Section key={ sectionKey } { ...section } index={ sectionIndex } />,
    [sectionIds.satisfactionGuaranteed]: <SatisfactionGuaranteedSection key={ sectionKey } { ...section } index={ sectionIndex } />,
    [sectionIds.loyalty]: <LoyaltySection key={ sectionKey } { ...section } index={ sectionIndex } />
  };

  if (section.type === sectionTypes.personal && showPersonalSectionBrowser) {
    return identifySections[section.type];
  }

  return identifySections[section.id];
};

function HomePage({
  loadHomePage,
  sections = [],
  t,
  loading
}) {
  const history = useNavigate();
  const location = useLocation();
  const { isMobile } = useMatchMediaQuery();

  const hasSectionsToLoad = sections.filter(s => s.refreshOnScroll).length > 0;

  useEffect(() => {
    loadHomePage(false);
    analyticsService.trackPageVisit('homepage');
  }, []);

  useEffect(() => {
    const needReloadHomePage = location.state?.needReloadHomePage;
    if (needReloadHomePage) {
      loadHomePage(needReloadHomePage);
      history(`${ location.pathname }${ location.search }`, {
        state: { ...location.state, needReloadHomePage: false },
        replace: true
      });
    }
  }, [location.state]);

  useEffect(() => {
    if (!hasSectionsToLoad) return () => {};

    const params = {
      section_ids: sections.filter(s => s.refreshOnScroll).map(s => s.id).join(),
      exclude_advisor_ids: sections.reduce((arr, section) => {
        if (section.content?.advisors?.length) {
          arr.push(...section.content.advisors.map(x => x.id));
        }
        return arr;
      }, []).join()
    };

    const scrollHandler = () => {
      const scrollPosition = window.innerHeight + document.documentElement.scrollTop;
      const threshold = 1200;
      if (scrollPosition >= threshold) {
        loadHomePage(false, params, true);
        window.removeEventListener('scroll', scrollHandler);
      }
    };

    window.addEventListener('scroll', scrollHandler);

    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [hasSectionsToLoad]);

  const renderSectionLoading = () => {
    const emptySectionArray = [
      { content: { advisors: advisorsEmptyList(2) } },
      { content: { advisors: advisorsEmptyList(2) } }
    ];
    return (
      emptySectionArray.map((emptySection, index) => (
        <AdvisorSection
          { ...emptySection }
          key={ `emptySection_${ index }` }
          theme={ advisorsSectionThemes.badge }
          personalSection={ false }
          loading={ loading }
        />
      ))
    );
  };

  const renderSections = () => {
    if (loading) return renderSectionLoading();
    let showPersonalSectionBrowser = !isMobile;
    let sectionIndex = -1;

    return sections.map((section, index) => {
      if (section.content?.advisors?.length) {
        sectionIndex++;
      }

      const identifySection = getIdentifySection(section, showPersonalSectionBrowser, sectionIndex, index);

      if (identifySection) {
        if (section.type === sectionTypes.personal
          && showPersonalSectionBrowser) { showPersonalSectionBrowser = false; }
        return identifySection;
      }
      return (
        <div key={ `${ section.id }_${ index }` } className={ classes.sectionsMainContainer } data-testid={ `HP-${ section.id }` }>
          <AdvisorSection
            { ...section }
            isRequired={ null }
            key={ `AdvisorSection_${ section.id }_${ index }` }
            theme={ getAdvisorSectionTheme(section) }
            personalSection={ false }
            loading={ loading }
            sectionIndex={ sectionIndex }
          />
        </div>
      );
    });
  };

  return (
    <AppLayout
      noNeedMarginTop
      needsHeaderTransparentTransition
      footerMobileBottomHeight={ 30 }
      itemProp={ itemProp }
    >
      <AffiliatePromoPopup />
      <Helmet>
        <title>{ t('app_meta_tag.psychics_page_title') }</title>
        <meta property="og:title" content={ t('app_meta_tag.psychics_page_title') } />
      </Helmet>
      { renderCanonicalLink() }
      <HeroBanner
        itemProp={ itemProp }
      />
      <div
        className={ classes.contentContainer }
      >
        <PromotionLabel className={ classes.promotionHolder } promotionLabelTypeScreen={ promotionLabelTypeScreens.homePage } />
        {renderSections()}
      </div>
    </AppLayout>
  );
}

export default HomePage;
